import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReportWorkflow } from '../../../../services/firestore';
import StatusBadge from '../../../../components/reports/StatusBadge';
import DeadlineBadge from '../../../../components/reports/DeadlineBadge';
import ErrorBoundary from '../../../../components/ErrorBoundary';
import { useRatedInstitutions } from '../../../../hooks/useInstitutions';
import LeftPanel from './LeftPanel';
import MiddlePanel from './MiddlePanel';
import BottomPanel from './BottomPanel';
import WorkflowReportPreview from './WorkflowReportPreview';
import './ReportEditorV2.css';

type ApprovalMode = 'none' | 'admin' | 'admin_skip_client' | 'committee';

interface ReportEditorV2Props {
  report: ReportWorkflow;
  onSave: (content: Record<string, string>) => Promise<void>;
  onSubmit: () => Promise<void>;
  isSaving?: boolean;
  // Approval props (optional - only for admin/committee views)
  approvalMode?: ApprovalMode;
  onApprove?: (comments?: string) => Promise<void>;
  onReject?: (reason: string) => Promise<void>;
  onSkipClientReview?: () => Promise<void>;
}

export default function ReportEditorV2({
  report,
  onSave,
  onSubmit,
  isSaving = false,
  approvalMode = 'none',
  onApprove,
  onReject,
  onSkipClientReview,
}: ReportEditorV2Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Get return URL from location state (passed when navigating from analyst bench)
  const returnUrl = (location.state as { returnUrl?: string })?.returnUrl || '/reports';

  // Fetch institution data from BigQuery API
  const { institutions } = useRatedInstitutions();

  // Create a lookup map for institution names
  const institutionNameMap = useMemo(() => {
    const map = new Map<string, string>();
    institutions.forEach((inst) => map.set(inst.institution_id, inst.institution_name));
    return map;
  }, [institutions]);

  // Active section state
  const [activeSection, setActiveSection] = useState<string>('activo');

  // Section content state - initialized from report.content.sections if available
  // Extract content strings from SectionDraft objects
  const [sectionContent, setSectionContent] = useState<Record<string, string>>(() => {
    const sections = report.content?.sections || {};
    const content: Record<string, string> = {};
    for (const [id, section] of Object.entries(sections)) {
      content[id] = section?.content || '';
    }
    return content;
  });

  // Bottom panel expansion state
  const [isBottomPanelExpanded, setIsBottomPanelExpanded] = useState(false);

  // Editor preview mode state (shared between MiddlePanel and LeftPanel)
  const [isPreviewMode, setIsPreviewMode] = useState(true);

  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Modal states
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Approval modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approvalComments, setApprovalComments] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Get institution name from BigQuery data
  const getInstitutionName = (institutionId: string): string => {
    return institutionNameMap.get(institutionId) || institutionId;
  };

  // Format period for display (e.g., "2024-03" → "March 2024")
  const formatPeriod = (period: string): string => {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US', { month: 'long', year: 'numeric' });
  };

  // Extract used assets from all section content
  const usedAssets = useMemo(() => {
    const allContent = Object.values(sectionContent).join('\n');
    const assets: string[] = [];

    // Match [CHART:grafico01] and [TABLE:cuadro01] patterns
    const chartMatches = allContent.matchAll(/\[CHART:(\w+)\]/g);
    const tableMatches = allContent.matchAll(/\[TABLE:(\w+)\]/g);

    for (const match of chartMatches) {
      if (!assets.includes(match[1])) {
        assets.push(match[1]);
      }
    }
    for (const match of tableMatches) {
      if (!assets.includes(match[1])) {
        assets.push(match[1]);
      }
    }

    return assets;
  }, [sectionContent]);

  // Handle section content change
  const handleSectionChange = useCallback((sectionId: string, content: string) => {
    setSectionContent(prev => ({
      ...prev,
      [sectionId]: content,
    }));
    setHasUnsavedChanges(true);
  }, []);

  // Handle section selection
  const handleSectionSelect = useCallback((sectionId: string) => {
    setActiveSection(sectionId);
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    try {
      await onSave(sectionContent);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving:', error);
      // Keep unsaved changes flag true if save failed
    }
  }, [onSave, sectionContent]);

  // Handle asset insertion
  const handleInsertAsset = useCallback((assetRef: string) => {
    // Insert asset reference at current cursor position
    setSectionContent(prev => ({
      ...prev,
      [activeSection]: (prev[activeSection] || '') + `\n${assetRef}\n`,
    }));
    setHasUnsavedChanges(true);
  }, [activeSection]);

  // Handle submit with confirmation
  const handleSubmitConfirm = useCallback(async () => {
    try {
      setIsSubmitting(true);
      await onSubmit();
      setShowSubmitConfirm(false);
    } catch (error) {
      console.error('Error submitting:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit]);

  // Handle approve
  const handleApprove = useCallback(async () => {
    if (!onApprove) return;
    try {
      setIsProcessing(true);
      await onApprove(approvalComments || undefined);
      setShowApproveModal(false);
      setApprovalComments('');
      navigate(returnUrl);
    } catch (error) {
      console.error('Error approving:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [onApprove, approvalComments, navigate, returnUrl]);

  // Handle reject
  const handleReject = useCallback(async () => {
    if (!onReject || !rejectionReason.trim()) return;
    try {
      setIsProcessing(true);
      await onReject(rejectionReason);
      setShowRejectModal(false);
      setRejectionReason('');
      navigate(returnUrl);
    } catch (error) {
      console.error('Error rejecting:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [onReject, rejectionReason, navigate, returnUrl]);

  // Handle skip client review
  const handleSkipClientReview = useCallback(async () => {
    if (!onSkipClientReview) return;
    try {
      setIsProcessing(true);
      await onSkipClientReview();
      navigate(returnUrl);
    } catch (error) {
      console.error('Error skipping client review:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [onSkipClientReview, navigate, returnUrl]);

  // Check which sections have content
  const completedSections = Object.entries(sectionContent)
    .filter(([, content]) => content && content.trim().length > 0)
    .map(([id]) => id);

  return (
    <div className="report-editor-v2">
      {/* Header */}
      <div className="editor-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate(returnUrl)}>
            {t('reportEditor.back')}
          </button>
          <div className="header-info">
            <h1>{getInstitutionName(report.institution_id)}</h1>
            <div className="header-meta">
              <span className="period">{formatPeriod(report.period)}</span>
              <StatusBadge status={report.status} />
              <DeadlineBadge
                dueDate={report.deadline.final_due_date}
                isCompleted={report.status === 'approved'}
                size="medium"
                showLabel
              />
            </div>
          </div>
        </div>
        <div className="header-right">
          {hasUnsavedChanges && (
            <span className="unsaved-indicator">{t('reportEditor.unsavedChanges')}</span>
          )}
          <button
            className="preview-btn"
            onClick={() => setShowPreview(true)}
          >
            {t('reportEditor.viewPreview')}
          </button>
          {report.status === 'draft' && (
            <>
              <button
                className="save-btn"
                onClick={handleSave}
                disabled={isSaving || !hasUnsavedChanges}
              >
                {isSaving ? t('reportEditor.saving') : t('reportEditor.saveDraft')}
              </button>
              <button
                className="submit-btn"
                onClick={() => setShowSubmitConfirm(true)}
                disabled={completedSections.length < 9}
              >
                {t('reportEditor.submitForReview')}
              </button>
            </>
          )}
          {/* Approval actions for admin/committee */}
          {approvalMode !== 'none' && (
            <div className="approval-actions">
              <div className="approval-buttons">
                <button
                  className="reject-btn"
                  onClick={() => setShowRejectModal(true)}
                  disabled={isProcessing}
                >
                  {t('reportEditor.reject')}
                </button>
                <button
                  className="approve-btn"
                  onClick={() => setShowApproveModal(true)}
                  disabled={isProcessing}
                >
                  {t('reportEditor.approve')}
                </button>
              </div>
              {approvalMode === 'admin_skip_client' && (
                <button
                  className="skip-btn"
                  onClick={handleSkipClientReview}
                  disabled={isProcessing}
                >
                  {t('reportEditor.skipClientReview')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main content area with 3 panels */}
      <div className="editor-body">
        {/* Left Panel - Outline & Assets */}
        <ErrorBoundary>
          <LeftPanel
            activeSection={activeSection}
            completedSections={completedSections}
            onSectionSelect={handleSectionSelect}
            onInsertAsset={handleInsertAsset}
            usedAssets={usedAssets}
            isReadOnly={report.status !== 'draft' || isPreviewMode}
          />
        </ErrorBoundary>

        {/* Middle Panel - Editor */}
        <ErrorBoundary>
          <MiddlePanel
            activeSection={activeSection}
            content={sectionContent[activeSection] || ''}
            onChange={(content) => handleSectionChange(activeSection, content)}
            isReadOnly={report.status !== 'draft'}
            institutionId={report.institution_id}
            period={report.period}
            isPreviewMode={isPreviewMode}
            onTogglePreview={() => setIsPreviewMode(!isPreviewMode)}
          />
        </ErrorBoundary>

        {/* Bottom Panel - Data Tables */}
        <ErrorBoundary>
          <BottomPanel
            institutionId={report.institution_id}
            reportPeriod={report.period}
            isExpanded={isBottomPanelExpanded}
            onToggleExpand={() => setIsBottomPanelExpanded(!isBottomPanelExpanded)}
          />
        </ErrorBoundary>
      </div>

      {/* Submit Confirmation Modal */}
      {showSubmitConfirm && (
        <div className="modal-overlay" onClick={() => setShowSubmitConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('reportEditor.submitConfirmTitle')}</h2>
            <p>
              {t('reportEditor.submitConfirmMessage')}
            </p>
            <div className="modal-stats">
              {t('reportEditor.sectionsCompleted', { completed: completedSections.length })}
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowSubmitConfirm(false)}
              >
                {t('reportEditor.cancel')}
              </button>
              <button
                className="confirm-btn"
                onClick={handleSubmitConfirm}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('reportEditor.submitting') : t('reportEditor.submitForReview')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <WorkflowReportPreview
          report={report}
          sectionContent={sectionContent}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="modal-overlay" onClick={() => setShowApproveModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('reportEditor.approveReport')}</h2>
            <p>
              {approvalMode === 'committee'
                ? t('reportEditor.approveCommitteeMessage')
                : t('reportEditor.approveAdminMessage')}
            </p>
            <div className="form-group">
              <label>{t('reportEditor.commentsOptional')}</label>
              <textarea
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
                placeholder={t('reportEditor.commentsPlaceholder')}
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowApproveModal(false)}
              >
                {t('reportEditor.cancel')}
              </button>
              <button
                className="confirm-btn"
                onClick={handleApprove}
                disabled={isProcessing}
              >
                {isProcessing ? t('reportEditor.processing') : t('reportEditor.confirmApproval')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('reportEditor.rejectReport')}</h2>
            <p className="modal-subtitle">
              {t('reportEditor.rejectMessage')}
            </p>
            <div className="form-group">
              <label>{t('reportEditor.rejectionReasonLabel')}</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder={t('reportEditor.rejectionPlaceholder')}
                rows={4}
              />
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowRejectModal(false)}
              >
                {t('reportEditor.cancel')}
              </button>
              <button
                className="reject-btn"
                onClick={handleReject}
                disabled={!rejectionReason.trim() || isProcessing}
              >
                {isProcessing ? t('reportEditor.rejecting') : t('reportEditor.confirmRejection')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
